import React, { useState, useEffect } from "react";
import APIService from "../../services/APIService";
import MemberViewAPI from "../../services/MemberViewAPI";
import { useUser } from "../../context/UserContext";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import RefreshButton from "../../common/RefreshButton";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "../css/DashboardSimple.css";
import "../css/SafetyScoreCard.css";

function EmailSimulations() {
  const { t } = useTranslation();
  const { isReadOnlyView, selectedUser } = useUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRecent, setShowRecent] = useState(false);

  useEffect(() => {
    fetchEmailStats();
  }, [isReadOnlyView, selectedUser?.uuid]);

  const fetchEmailStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      let response;
      if (isReadOnlyView && selectedUser?.uuid) {
        response = await MemberViewAPI.getMemberEmailStats(selectedUser.uuid);
      } else {
        response = await APIService.get(
          "/api/v1/simulation/email-simulation-stats",
        );
      }

      if (response?.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching email stats:", error);
      setError(error.message);
      setStats({
        totalSent: 0,
        totalClicked: 0,
        avoided: 0,
        recentSimulations: [],
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Use centralized DateTimeUtil for consistent date formatting
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return formatDateOnly(dateString, "medium");
  };

  const handleRefresh = () => {
    fetchEmailStats(true);
  };

  const getScoreExplanation = () => {
    if (!stats || stats.totalSent === 0) {
      return t("dashboard.emailSims.explain0");
    }
    const avoidRate = (stats.avoided / stats.totalSent) * 100;
    if (avoidRate === 100) {
      return t("dashboard.emailSims.explainPerfect");
    } else if (avoidRate >= 80) {
      return t("dashboard.emailSims.explain80");
    } else if (avoidRate >= 60) {
      return t("dashboard.emailSims.explain60");
    } else {
      return t("dashboard.emailSims.explainLow");
    }
  };

  if (loading && !stats) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">{t("dashboard.emailSims.title")}</h2>
        <LoadingSpinner
          message={t("common.loading")}
          size="md"
          className="compact"
        />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">{t("dashboard.emailSims.title")}</h2>
        <div className="safety-error-message">
          <p className="safety-error-text">{t("common.unableToLoad")}</p>
          <button
            onClick={() => fetchEmailStats()}
            className="safety-retry-btn"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  const hasData = stats && stats.totalSent > 0;

  return (
    <div className="simple-card safety-score-card">
      <div className="safety-card-header">
        <h2 className="simple-card-title">{t("dashboard.emailSims.title")}</h2>
        <RefreshButton
          onClick={handleRefresh}
          loading={isRefreshing}
          size="sm"
        />
      </div>

      {hasData ? (
        <>
          {/* Main Score - Large and Prominent */}
          <div className="shield-score-hero">
            <span className="shield-score-value">
              {stats.avoided}/{stats.totalSent}
            </span>
            <span className="shield-score-period">
              {t("dashboard.emailSims.phishingAvoided")}
            </span>
          </div>

          {/* Score Explanation */}
          <p className="shield-explanation">{getScoreExplanation()}</p>

          {/* Stats Row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              padding: "0.75rem",
              backgroundColor: "var(--progress-bg)",
              borderRadius: "8px",
              marginBottom: "1rem",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "600",
                  color: "#10b981",
                }}
              >
                {stats.avoided}
              </div>
              <div style={{ fontSize: "0.95rem", color: "#4b5563" }}>
                {t("dashboard.emailSims.avoided")}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "600",
                  color: stats.totalClicked > 0 ? "#ef4444" : "#6b7280",
                }}
              >
                {stats.totalClicked}
              </div>
              <div style={{ fontSize: "0.95rem", color: "#4b5563" }}>
                {t("dashboard.emailSims.clicked")}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "1.4rem",
                  fontWeight: "600",
                  color: "#3b82f6",
                }}
              >
                {stats.totalSent}
              </div>
              <div style={{ fontSize: "0.95rem", color: "#4b5563" }}>
                {t("dashboard.emailSims.total")}
              </div>
            </div>
          </div>

          {/* Recent Tests — all hidden behind expandable dropdown */}
          {stats.recentSimulations?.length > 0 && (
            <div className="shield-areas-section">
              <button
                className="shield-areas-toggle"
                onClick={() => setShowRecent(!showRecent)}
                style={{ marginTop: "0.5rem" }}
              >
                {showRecent ? "▼" : "▶"}{" "}
                {t("dashboard.emailSims.showTests").replace(
                  "{count}",
                  stats.recentSimulations.length,
                )}
              </button>

              {showRecent && (
                <div
                  className="sim-recent-list"
                  style={{ marginTop: "0.5rem" }}
                >
                  {stats.recentSimulations.map((sim, index) => (
                    <div
                      key={sim.id || index}
                      className={`sim-recent-item ${sim.clicked ? "sim-item-clicked" : "sim-item-avoided"}`}
                    >
                      <span
                        className={sim.clicked ? "sim-clicked" : "sim-avoided"}
                      >
                        {sim.clicked ? "❌" : "✅"}
                      </span>
                      <span className="sim-content">
                        {sim.contentSnippet ||
                          t("dashboard.emailSims.emailTest")}
                      </span>
                      <span className="sim-date">{formatDate(sim.sentAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Empty State - Match other cards */}
          <div className="shield-score-hero">
            <span className="shield-score-value">—</span>
            <span className="shield-score-period">
              {t("dashboard.emailSims.noTestsYet")}
            </span>
          </div>
          <p className="shield-explanation">{getScoreExplanation()}</p>
        </>
      )}
    </div>
  );
}

export default EmailSimulations;
